/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { FileTransferRow, File as PrismaFile } from '.prisma/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography, makeStyles } from '@material-ui/core';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { SavedFile } from '../interfaces';
import { ContactPersonsOption } from '../interfaces/contact-persons';
import { schemaValidation } from '../lib/schema-validation';
import { formSchema } from '../schemas/form-schema';
import theme from '../styles/theme';
import { SubmitForm } from '../use-case/client/submit';
import DropZone from './dropzone';
import FieldComponents from './field-components';
import GoogleRecaptchaText from './google-recaptcha-text';
import ServiceError from './service-error';

const useStyles = makeStyles(() => ({
  dropzone: {
    minHeight: 655,
    maxWidth: 579,
    border: '1px dashed',
    borderColor: (props: StyleProps) => (props.fileError ? 'red' : '#00000080'),
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    flex: '1 1 auto',
    marginTop: 30,
    cursor: 'pointer',
  },
  dropzoneContainer: {
    height: '100%',
    maxWidth: 579,
  },
  dropzoneText: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '1.25rem',
    letterSpacing: '0.19px',
    lineHeight: '1.24em',
    color: '#707070',
    width: 200,
    textAlign: 'center',
  },
  dropzoneSpan: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '1.25rem',
    letterSpacing: '0.19px',
    lineHeight: '1.24em',
    color: '#E20815',
  },
  leftContainer: {
    width: '100%',
    marginRight: 30,
  },
  rightContainer: {
    width: '100%',
  },

  arrowIcon: {
    height: '116px',
    width: '120px',
    marginTop: '40px',
  },
  formFieldContainer: {
    marginTop: 60,
  },
  buttonContainer: {
    marginTop: 60,
  },
  step: {
    fontSize: '0.75rem',
    letterSpacing: '0.24px',
    lineHeight: '1.15em',
    fontFamily: 'Inter, sans-serif',
    textTransform: 'uppercase',
    color: '#000000DE',
  },
  stepTitle: {
    fontSize: '1.875rem',
    letterSpacing: '-0.26px',
    lineHeight: '1.35em',
    fontWeight: 300,
    color: '#000000DE',
  },
  submitButton: {
    width: 180,
    background: '#4B71EF',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  cancleSubmit: {
    width: 180,
    background: 'red',
    marginLeft: '15px',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  fileError: {
    color: 'red',
  },
  loadginText: {
    color: '#707070',
    fontSize: '0.75rem',
    letterSpacing: '0.12px',
    marginBottom: '20px',
  },
  finishedText: {
    color: '#4B71EF',
    fontSize: '0.75rem',
    letterSpacing: '0.12px',
    marginBottom: '20px',
  },
  uploadPercent: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 900,
    marginRight: '10px',
  },
  inputContainer: {
    flexDirection: 'row',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  mobileFileButton: {
    color: 'white',
    backgroundColor: '#880226',
    height: '48px',
    width: '214px',
    '&:hover': {
      backgroundColor: 'rgba(136, 2, 38,0.9)',
    },
  },
  deleteButton: {
    borderRadius: '40px',
    paddingLeft: '15px',
    paddingRight: '15px',
    marginLeft: '10px',
    border: '0.5px solid #B1B8C0',
    background: '#B1B8C026',
    height: '20px',
    color: '#707070',
    textTransform: 'uppercase',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.625rem',
    cursor: 'pointer',
  },
  progressContainer: {
    height: '20px',
    width: '100%',

    display: 'flex',
    flexDirection: 'row',
    marginTop: '10px',
    alignContent: 'center',
  },
  progressBar: {
    height: '100%',
    width: '100%',
  },
  progressColor: { background: '#4B71EF' },
  progressLabel: {
    fontSize: '0.625rem',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 'bold',
    textAlign: 'end',
  },
}));

interface Props {
  rowId: string;
  csrfToken: string;
  gdpr?: boolean;
  savedFiles?: string;
  contactPersons: ContactPersonsOption[];
  materialRequestRow?: FileTransferRow;
}

interface StyleProps {
  fileError: boolean;
}

const FileTransferForm: FC<Props> = ({
  rowId,
  csrfToken,
  gdpr = false,
  savedFiles,
  contactPersons,
  materialRequestRow,
}: Props) => {
  const schema = formSchema(contactPersons, gdpr);

  const { t } = useTranslation(['form', 'validation']);
  const router = useRouter();
  const schemaObject = schemaValidation(schema, t);
  const validationSchema = yup.object().shape({ ...schemaObject }, [['job', 'offerNumber']]);

  const [files, setFiles] = useState<SavedFile[]>([]);
  const [csrf, setCsrf] = useState(csrfToken);
  const [fileError, setFileError] = useState<string>();
  const classes = useStyles({ fileError: !!fileError });

  const [uploading, setUploading] = useState(false);
  const [submitAfterFiles, setSubmitAfterFiles] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showError, setShowError] = useState(false);
  const { register, handleSubmit, getValues, reset, control, formState } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const handleWindowClose = (e: BeforeUnloadEvent) => {
    const { dirtyFields } = formState;
    const preventCheck = uploading || (dirtyFields && Object.keys(dirtyFields).length > 0);
    if (preventCheck) e.preventDefault();
    const warningText = t('confirm');
    return (e.returnValue = warningText);
  };

  const handleBrowseAway = () => {
    const { dirtyFields } = formState;
    const preventCheck = uploading || (dirtyFields && Object.keys(dirtyFields).length > 0);
    if (!preventCheck) return;
    const warningText = t('confirm');
    if (window.confirm(warningText)) return;
    router.events.emit('routeChangeError');
    throw 'routeChange aborted.';
  };

  useEffect(() => {
    const { isSubmitting, isSubmitted } = formState;

    if ((!isSubmitting && !isSubmitted) || uploading) {
      window.addEventListener('beforeunload', handleWindowClose);
      router.events.on('routeChangeStart', handleBrowseAway);

      return () => {
        window.removeEventListener('beforeunload', handleWindowClose);
        router.events.off('routeChangeStart', handleBrowseAway);
      };
    }
  }, [formState, uploading]);

  useEffect(() => {
    if (materialRequestRow) {
      const newFormData = {
        ...materialRequestRow,
        job: materialRequestRow.multipressJobNumber || materialRequestRow.job,
        contactPerson: {
          label: materialRequestRow.contactName,
          value: materialRequestRow.contactEmail,
        },
      };

      reset(newFormData);
      return;
    }

    const localData = localStorage.getItem('formData');
    const array = savedFiles ? JSON.parse(savedFiles) : null;

    let filesArray: SavedFile[] = [];
    if (array) {
      const mappedArray = array.map((file: PrismaFile) => ({
        path: file.path,
        originalName: file.originalName,
        id: file.id,
        uuid: file.uuid,
        uploadPercent: 100,
      }));
      filesArray = [...mappedArray];
    }

    if (localData) {
      const data = JSON.parse(localData);
      const savedData = data[rowId];
      if (savedData) {
        reset(savedData.formData);
      }
    } else {
      localStorage.removeItem('formData');
    }

    setFiles(filesArray);
  }, [reset]);

  const toLocalStorage = () => {
    const formData = getValues();
    const data = { [rowId]: { formData, files } };
    localStorage.setItem('formData', JSON.stringify(data));
  };

  const onSubmit = async (data: any) => {
    if (uploading) {
      setSubmitAfterFiles(true);
    } else {
      if (files.length > 0) {
        const token = executeRecaptcha ? await executeRecaptcha('form') : '';
        data.gdpr = gdpr;

        data.language = router.locale ? router.locale : 'fi';
        const response = await SubmitForm(rowId, data, token, csrf);

        if (response) {
          const data = await response.json();
          localStorage.removeItem('formData');
          setCsrf(data.newCsfrToken);
          window.removeEventListener('beforeunload', handleWindowClose);
          router.events.off('routeChangeStart', handleBrowseAway);
          router.push({ pathname: '/confirm', query: { rowId } });
        } else {
          setShowError(true);
        }
      } else {
        setFileError('noFiles');
      }
    }
  };

  useEffect(() => {
    toLocalStorage();

    if (files && files.length > 0 && !uploading && submitAfterFiles) {
      handleSubmit(onSubmit)();
    }
    if (files && files.length === 0) setSubmitAfterFiles(false);
  }, [files, uploading]);

  const mapSchema = () => {
    return schema.map((field, i) => {
      if (field.dontShow) return <></>;
      return (
        <FieldComponents
          key={field.name + i}
          field={field}
          t={t}
          register={register}
          errors={formState.errors}
          toLocal={toLocalStorage}
          control={control}
          disabled={formState.isSubmitting || submitAfterFiles}
        ></FieldComponents>
      );
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="form-container">
        <div className="left-container">
          <Grid container direction="column" className={classes.dropzoneContainer}>
            <div className="step">
              <Typography className={classes.step}>{t('dropzone.step')}</Typography>
              <Typography className={classes.stepTitle}>{t('dropzone.stepTitle')}</Typography>
            </div>

            <DropZone
              fileErrorText={fileError}
              files={files}
              setFiles={setFiles}
              csrf={csrf}
              setCsrf={setCsrf}
              isSubmitting={formState.isSubmitting}
              setUploading={setUploading}
              uploading={uploading}
              submitAfterFiles={submitAfterFiles}
              gdpr={gdpr}
            ></DropZone>
            {!gdpr && (
              <p
                style={{
                  margin: '0',
                  marginTop: '10px',
                  fontSize: '16px',
                }}
              >
                {t('fileStoreText')}
              </p>
            )}

            {gdpr && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'end',
                  marginTop: '10px',
                  color: 'blue',
                }}
              >
                <a
                  target={'_blank'}
                  href="https://punamusta.com/aineisto-ohjeet/inkjet-ohjeet/"
                  rel="noreferrer"
                >
                  {t('instructionLink')}
                </a>
              </div>
            )}
          </Grid>
        </div>
        <Grid className={classes.rightContainer} item>
          <div className="step">
            <Typography className={classes.step}>{t('textFields.step')}</Typography>
            <Typography className={classes.stepTitle}>{t('textFields.stepTitle')}</Typography>
          </div>

          <Grid className={classes.formFieldContainer} container direction="column">
            <Grid className={classes.inputContainer} container direction="row" spacing={4}>
              {mapSchema()}
            </Grid>
            <div className="submit-container">
              <div style={{ marginBottom: 10 }}>{t('requiredInfo')}</div>

              <div className="button-container">
                <Button
                  className={classes.submitButton}
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={formState.isSubmitting || submitAfterFiles}
                >
                  {t('textFields.submit')}
                </Button>
                {submitAfterFiles && (
                  <Button
                    className={classes.cancleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={formState.isSubmitting}
                    type="button"
                    onClick={() => {
                      setSubmitAfterFiles(false);
                    }}
                  >
                    {t('textFields.cancelSubmit')}
                  </Button>
                )}
              </div>
            </div>

            <GoogleRecaptchaText></GoogleRecaptchaText>
          </Grid>
        </Grid>
        <ServiceError show={showError}></ServiceError>
      </div>
      <style jsx>{`
        .form-container {
          height: 100%;
          min-height: 100%;
          display: flex;
          flex-direction: column;
        }

        form {
          width: 100%;
        }
        .left-container {
          width: 100%;
        }
        .submit-container {
          display: flex;
          flex-direction: column;
          margin-top: 60px;
        }
        .button-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
        .step {
          display: none;
        }
        @media only screen and (min-width: ${theme.breakpoints.values.sm}px) {
          .step {
            display: block;
            margin-top: 20px;
          }
          .dropzone-mobile-container {
            display: none;
          }
          .submit-container {
            align-items: flex-end;
          }
          .button-container {
            justify-content: flex-end;
          }
        }

        @media only screen and (min-width: ${theme.breakpoints.values.md}px) {
          form {
            width: initial;
          }
          .form-container {
            flex-direction: row;
          }
          .submit-container {
            align-items: flex-start;
          }
          .button-container {
            justify-content: flex-start;
          }

          .step {
            display: block;
          }
          .left-container {
            width: 100%;
            margin-right: 30px;
          }
        }
      `}</style>
    </form>
  );
};

export default FileTransferForm;
